import { useState } from 'react';
import { HiOutlinePlus, HiOutlineArrowPath, HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { usePlaces, useSearch } from '../../../../hooks';
import { Alert, Modal } from '../../../commons';
import { DAV_APIS } from '../../../../adapters';
import DashboardLoader from '../../sections/Loader/DashboardLoader';
import { Card } from '../../../ui/Card';
import { Button } from '../../../ui/Button';
import PlaceCard from './PlaceCard';

const Places = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlace, setSelectedPlace] = useState('');
  const [refresh, setRefresh] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const { places, loading } = usePlaces(refresh);
  const navigate = useNavigate();

  const placeResults = useSearch(places, searchTerm);
  const isList = layout === 'list';

  const handleDeletePlace = async () => {
    setIsLoading(true);
    try {
      const res = await DAV_APIS.place.deletePlace(selectedPlace);
      if (res.status === 204) {
        Alert({ message: 'Deleted Successfully', variant: 'success' });
      }
      setIsLoading(false);
      setShowModal(false);
      setRefresh(Math.random());
    } catch (error) {
      setIsLoading(false);
      setShowModal(false);
      Alert({ message: 'Failed to delete place', variant: 'error' });
    }
  };

  const onDeleteHandler = (placeId: string) => {
    setSelectedPlace(placeId);
    setShowModal(true);
  };

  const onEditHandler = (placeId: string) => {
    navigate(`/dashboard/places/edit?place=${placeId}`);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className='flex flex-col items-center justify-center py-20 animate-pulse'>
          <DashboardLoader />
        </div>
      );
    }

    if (placeResults?.length > 0) {
      return (
        <div className={`grid gap-6 ${isList ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'}`}>
          {placeResults.map((place: any, index: number) => (
            <div key={place.id} style={{ animationDelay: `${index * 50}ms` }} className='animate-in fade-in slide-in-from-bottom-2 duration-500'>
              <PlaceCard
                place={place}
                layout={layout}
                onEditHandler={() => onEditHandler(place.id)}
                onDeleteHandler={() => onDeleteHandler(place.id)}
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <Card variant='glass' className='py-20 flex flex-col items-center justify-center text-center'>
        <div className='w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4'>
          <HiOutlineMagnifyingGlass className='w-10 h-10 text-gray-400' />
        </div>
        <h3 className='text-lg font-bold text-gray-900 dark:text-white'>No places found</h3>
        <p className='text-gray-500 dark:text-gray-400 max-w-sm mt-1 px-6'>
          {searchTerm ?
            `We couldn't find any places matching "${searchTerm}".`
            : `There are currently no places configured.`}
        </p>
      </Card>
    );
  };

  return (
    <div className='space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight'>
            Explore
          </h1>
          <p className='text-gray-500 dark:text-gray-400 mt-1 max-w-2xl'>
            Manage destinations and places featured across safari experiences.
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <Button
            variant='primary'
            onClick={() => navigate('/dashboard/places/new')}
            leftIcon={<HiOutlinePlus className='w-5 h-5' />}
          >
            Add Place
          </Button>
        </div>
      </div>

      <Card variant='glass' className='p-4 border-none shadow-lg shadow-gray-200/40 dark:shadow-none'>
        <div className='flex flex-wrap items-center gap-4'>
          <div className='relative flex-1 min-w-[240px]'>
            <div className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400'>
              <HiOutlineMagnifyingGlass className='w-5 h-5' />
            </div>
            <input
              type='text'
              placeholder='Search places...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='w-full pl-11 pr-4 py-2.5 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 placeholder:text-gray-400 dark:placeholder:text-gray-500 transition-all duration-200'
            />
          </div>

          <div className='flex items-center gap-3 ml-auto'>
            <div className='flex items-center p-1 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700/50'>
              <button
                type='button'
                onClick={() => setLayout('grid')}
                className={`p-1.5 rounded-md transition-all duration-200 ${layout === 'grid' ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title='Grid View'
              >
                <HiOutlineViewGrid className='w-5 h-5' />
              </button>
              <button
                type='button'
                onClick={() => setLayout('list')}
                className={`p-1.5 rounded-md transition-all duration-200 ${layout === 'list' ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title='List View'
              >
                <HiOutlineViewList className='w-5 h-5' />
              </button>
            </div>

            <button
              type='button'
              onClick={() => setRefresh(Math.random())}
              disabled={loading}
              className={`p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400 transition-all duration-200 shadow-sm ${loading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
              title='Refresh list'
            >
              <HiOutlineArrowPath className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </Card>

      {renderContent()}

      <Modal
        size='sm'
        open={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleDeletePlace}
        loading={isLoading}
        successText='Delete Place'
        loadingText='Deleting...'
      >
        <div className='space-y-3'>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>Remove Place</h2>
          <p className='text-gray-600 dark:text-gray-400 leading-relaxed'>
            Are you sure you want to delete this place? This action cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Places;
